import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getReturns, createReturn, actOnReturn, getInventoryItems, getStockBatches, getItemTypes } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Plus, PackageCheck, RefreshCw, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'merchant_request', label: 'Merchant Request' },
  { value: 'inactive', label: 'Inactive / Not transacting' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'other', label: 'Other' },
];

const STATUS_TABS = ['all', 'pending', 'approved', 'rejected', 'completed'];

const Returns = ({ user, setUser }) => {
  const [returns, setReturns] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [tab, setTab] = useState('pending');
  const [open, setOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [acting, setActing] = useState(null);
  const [actNotes, setActNotes] = useState('');
  const [form, setForm] = useState({ item_id: '', batch_id: '', quantity: 1, reason: 'defective', notes: '', request_replacement: false, replacement_item_type_id: '', kind: 'serialized' });

  const isAdmin = ['admin', 'hr_manager', 'warehouse_manager'].includes(user?.role);

  const load = async () => {
    try {
      const [r, i, b, t] = await Promise.all([
        getReturns(tab === 'all' ? {} : { status: tab }),
        getInventoryItems({ limit: 1000 }),
        getStockBatches({}),
        getItemTypes(),
      ]);
      setReturns(r.data); setItems(i.data); setBatches(b.data); setItemTypes(t.data);
    } catch { toast.error('Failed to load returns'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const submit = async () => {
    try {
      const payload = {
        quantity: parseInt(form.quantity) || 1,
        reason: form.reason,
        notes: form.notes,
        request_replacement: form.request_replacement,
        replacement_item_type_id: form.replacement_item_type_id || null,
      };
      if (form.kind === 'serialized') payload.item_id = form.item_id;
      else payload.batch_id = form.batch_id;
      
      await createReturn(payload);
      toast.success('Return request created');
      setOpen(false);
      setForm({ item_id: '', batch_id: '', quantity: 1, reason: 'defective', notes: '', request_replacement: false, replacement_item_type_id: '', kind: 'serialized' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doAct = async (action) => {
    try {
      await actOnReturn(acting.id, { action, notes: actNotes });
      toast.success(`Return ${action}`);
      setActOpen(false); setActing(null); setActNotes('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Returns & Replacements</h1>
            <p className="text-sm text-slate-500">Request return, scrap, or replacement of an asset</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-return-btn"><Plus className="h-4 w-4 mr-1" />New Return Request</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Return Request</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Type</Label>
                  <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v, item_id: '', batch_id: '' })}>
                    <SelectTrigger data-testid="ret-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="serialized">Serialized Item</SelectItem>
                      <SelectItem value="bulk">Bulk Batch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.kind === 'serialized' ? (
                  <div><Label>Item *</Label>
                    <Select value={form.item_id} onValueChange={v => setForm({ ...form, item_id: v })}>
                      <SelectTrigger data-testid="ret-item"><SelectValue placeholder="Choose item..." /></SelectTrigger>
                      <SelectContent>{items.filter(i => !['scrapped', 'lost'].includes(i.status)).map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.serial_number} ({i.status})</SelectItem>
                      ))}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div><Label>Batch *</Label>
                      <Select value={form.batch_id} onValueChange={v => setForm({ ...form, batch_id: v })}>
                        <SelectTrigger data-testid="ret-batch"><SelectValue placeholder="Choose batch..." /></SelectTrigger>
                        <SelectContent>{batches.map(b => {
                          const tn = itemTypes.find(t => t.id === b.item_type_id)?.name;
                          return <SelectItem key={b.id} value={b.id}>{tn} — {b.holder_name} ({b.quantity_remaining})</SelectItem>;
                        })}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></div>
                  </>
                )}
                <div><Label>Reason *</Label>
                  <Select value={form.reason} onValueChange={v => setForm({ ...form, reason: v })}>
                    <SelectTrigger data-testid="ret-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>{REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="ret-notes" /></div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="rr" checked={form.request_replacement} onChange={e => setForm({ ...form, request_replacement: e.target.checked })} />
                  <Label htmlFor="rr" className="cursor-pointer">Request replacement</Label>
                </div>
                {form.request_replacement && (
                  <div><Label>Replacement Item Type</Label>
                    <Select value={form.replacement_item_type_id} onValueChange={v => setForm({ ...form, replacement_item_type_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                      <SelectContent>{itemTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={submit} disabled={form.kind === 'serialized' ? !form.item_id : !form.batch_id} data-testid="ret-save">Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {STATUS_TABS.map(s => <TabsTrigger key={s} value={s} data-testid={`tab-${s}`} className="capitalize">{s === 'all' ? 'All' : s}</TabsTrigger>)}
          </TabsList>
          <TabsContent value={tab}>
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Item</th>
                        <th className="text-left p-2 text-xs font-medium">Quantity</th>
                        <th className="text-left p-2 text-xs font-medium">Reason</th>
                        <th className="text-left p-2 text-xs font-medium">Requested By</th>
                        <th className="text-left p-2 text-xs font-medium">Status</th>
                        <th className="text-left p-2 text-xs font-medium">Created</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {returns.map(r => (
                        <tr key={r.id} className="border-b">
                          <td className="p-2 text-xs">
                            <p className="font-medium">{r.item_type_name || '-'}</p>
                            {r.serial_number && <p className="font-mono text-indigo-700">{r.serial_number}</p>}
                          </td>
                          <td className="p-2">{r.quantity}</td>
                          <td className="p-2 capitalize">{(r.reason || '').replace('_', ' ')}</td>
                          <td className="p-2 text-xs">{r.requested_by_name}</td>
                          <td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${
                            r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            r.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                            r.status === 'completed' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>{r.status}</span></td>
                          <td className="p-2 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                          <td className="p-2 text-right">
                            {isAdmin && r.status === 'pending' && (
                              <Button size="sm" variant="outline" onClick={() => { setActing(r); setActOpen(true); }} data-testid={`act-${r.id}`}>Action</Button>
                            )}
                            {isAdmin && r.status === 'approved' && (
                              <Button size="sm" variant="outline" onClick={() => { setActing(r); setActOpen(true); }}>Complete</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {returns.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">No returns in this view</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={actOpen} onOpenChange={setActOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Action on Return Request</DialogTitle></DialogHeader>
            {acting && (
              <div className="space-y-3">
                <div className="p-3 bg-slate-50 rounded">
                  <p className="text-sm font-medium">{acting.item_type_name} {acting.serial_number && `(${acting.serial_number})`}</p>
                  <p className="text-xs text-slate-500">Reason: {(acting.reason || '').replace('_', ' ')}</p>
                  <p className="text-xs text-slate-500">Quantity: {acting.quantity}</p>
                </div>
                <div><Label>Admin Notes</Label><Input value={actNotes} onChange={e => setActNotes(e.target.value)} data-testid="act-notes" /></div>
              </div>
            )}
            <DialogFooter className="flex gap-2 justify-end">
              {acting?.status === 'pending' && (<>
                <Button variant="outline" onClick={() => doAct('reject')} data-testid="act-reject"><X className="h-4 w-4 mr-1" />Reject</Button>
                <Button onClick={() => doAct('approve')} data-testid="act-approve"><Check className="h-4 w-4 mr-1" />Approve</Button>
              </>)}
              {acting?.status === 'approved' && <Button onClick={() => doAct('complete')} data-testid="act-complete"><PackageCheck className="h-4 w-4 mr-1" />Mark Complete</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Returns;
